<template>
    <div class="">
        <div class="rounded-xl shadow-2xl overflow-hidden relative group ">

            <!-- Version normale -->
            <div v-if="!loading">
                <!-- Étiquette avec animation -->
                <span class="absolute top-3 left-3 px-4 py-2 bg-[#2A4493] text-white text-xs font-bold rounded-full z-1 hover:scale-110 shadow-lg">
                    {{ category.toUpperCase() }}
                </span>

                <!-- Overlay dégradé sur l'image -->
                <div class="relative overflow-hidden">
                    <img :src="source"
                         alt="Festival image"
                         class="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-110" />

                </div>

                <!-- Contenu avec espacement amélioré -->
                <div class="p-6 space-y-4 relative">

                    <!-- Titre avec effet gradient -->
                    <h3 class="text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent leading-snug clamp-title">
                        {{ title }}
                    </h3>

                    <!-- Date avec icône animée -->
                    <div class="flex items-center text-sm text-gray-600 gap-3 group/date">
                        <div class="flex items-center justify-center w-8 h-8 bg-[#2A4493] rounded-full transform transition-all duration-300 group-hover/date:rotate-12 group-hover/date:scale-110">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M8 7V3m8 4V3m-9 8h10m-13 4h16a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <!-- Date / Heure -->
                        <span class="font-medium" v-if="formatDate(date) === formatTime(endDate)">
                            {{ formatDate(date) + ' ' + formatTime(date) + ' - ' + formatTime(endDate)}}
                        </span>
                        <span class="font-medium" v-else>
                            {{ formatDate(date) + ' ' + formatTime(date) + ' à '}} <br>
                            {{ formatDate(endDate) + ' ' + formatTime(endDate)}}
                        </span>

                    </div>

                    <!-- Description avec meilleure lisibilité -->
                    <p class="text-sm text-gray-700 leading-relaxed text-justify clamp-description">
                        {{ description }}
                    </p>


                    <!-- Bouton d'action attractif -->
                    <div class="pt-2">
                        <button @click="$emit('action')" class="w-full bg-transparent text-[#2A4493] border border-[#2A4493] cursor-pointer font-semibold py-3 px-6 rounded-lg hover:shadow-lg hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-orange-300 transition-all duration-300">
                            <span class="flex items-center justify-center gap-2">
                                En savoir plus
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Version shimmer -->
            <div v-else>
                <!-- Étiquette shimmer -->
                <div class="absolute top-3 left-3 z-10">
                    <div class="shimmer shimmer-badge"></div>
                </div>

                <!-- Image shimmer -->
                <div class="relative overflow-hidden">
                    <div class="shimmer shimmer-image"></div>
                </div>

                <!-- Contenu shimmer -->
                <div class="p-6 space-y-4 relative">

                    <!-- Titre shimmer -->
                    <div class="space-y-2">
                        <div class="shimmer shimmer-title"></div>
                        <div class="shimmer shimmer-title-short"></div>
                    </div>

                    <!-- Date shimmer -->
                    <div class="flex items-center gap-3">
                        <div class="shimmer shimmer-icon-circle"></div>
                        <div class="shimmer shimmer-date"></div>
                    </div>

                    <!-- Description shimmer -->
                    <div class="space-y-2">
                        <div class="shimmer shimmer-description"></div>
                        <div class="shimmer shimmer-description-medium"></div>
                        <div class="shimmer shimmer-description-short"></div>
                        <div class="shimmer shimmer-sub-description"></div>
                    </div>

                    <!-- Bouton shimmer -->
                    <div class="pt-2">
                        <div class="shimmer shimmer-button"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
const props = defineProps({
    source: {
        type: String,
        default: ''
    },
    loading: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: ''
    },
    category: {
        type: String,
        default: ''
    },
    date: {
        type: String,
        default: null
    },
    endDate: {
        type: String,
        default: null
    },
    description: {
        type: String,
        default: ''
    },
})

const emit = defineEmits(['action'])

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR')
}

const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
    })
}

</script>

<style scoped>
.shimmer {
    display: inline-block;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmerAnim 1.5s infinite;
    border-radius: 6px;
}

.shimmer-badge {
    height: 28px;
    width: 80px;
    border-radius: 9999px;
}

.shimmer-image {
    height: 208px; /* h-52 équivalent */
    width: 100%;
    border-radius: 0;
}

.shimmer-title {
    height: 20px;
    width: 100%;
}

.shimmer-title-short {
    height: 20px;
    width: 75%;
}

.shimmer-icon-circle {
    height: 32px; /* w-8 h-8 */
    width: 32px;
    border-radius: 50%;
}

.shimmer-date {
    height: 16px;
    width: 100px;
}

.shimmer-description {
    height: 14px;
    width: 100%;
}

.shimmer-description-medium {
    height: 14px;
    width: 85%;
}

.shimmer-description-short {
    height: 14px;
    width: 60%;
}

.shimmer-sub-description {
    height: 14px;
    width: 90%;
    margin-top: 8px;
}

.shimmer-button {
    height: 48px; /* py-3 équivalent */
    width: 100%;
    border-radius: 8px;
}

@keyframes shimmerAnim {
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
}

/* Animation de brillance pour la version normale */
.group:hover .shimmer-overlay {
    transform: translateX(100%);
}

.shimmer-overlay {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    transition: transform 0.6s ease-in-out;
}


.clamp-title {
    display: -webkit-box;
    -webkit-line-clamp: 2;      /* Force 2 lignes max */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 2.6em;          /* Hauteur forcée pour 2 lignes */
    line-height: 1.3em;         /* Hauteur de ligne */
}

.clamp-description {
    display: -webkit-box;
    -webkit-line-clamp: 3;   /* Limite à 2 lignes */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 2.6em;       /* Assure une hauteur fixe */
    line-height: 1.3em;
}

</style>
